import maplibregl from 'maplibre-gl';
import { GeoJsonProperties, Geometry, Feature } from 'geojson';

import { ArtifactIconLinkType } from './mapTypes';
import { LAYER_VISIBILITY_ZOOM } from './mapConstants';

// GEOJSON UTILITIES

const makeFeatureCollection = (features: Feature<Geometry, GeoJsonProperties>[]) => ({
  type: 'FeatureCollection' as const,
  features
});

export const drawLines = (
  map: { current: maplibregl.Map | null },
  featuresArray: Feature<Geometry, GeoJsonProperties>[],
  uniqueId: string
) => {
  const existing = map.current?.getSource(uniqueId) as maplibregl.GeoJSONSource | undefined;
  if (existing) {
    existing.setData(makeFeatureCollection(featuresArray));
  } else {
    map.current?.addSource(uniqueId, {
      type: 'geojson',
      promoteId: 'uniqueFeatureId',
      data: makeFeatureCollection(featuresArray)
    });
  }

  if (!map.current?.getLayer(uniqueId)) {
    map.current?.addLayer({
      id: uniqueId,
      type: 'line',
      source: uniqueId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      minzoom: LAYER_VISIBILITY_ZOOM.lines.minzoom,
      paint: {
        'line-color': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          ['get', 'color'],
          ['get', 'color']
        ],
        'line-width': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          8,
          ['coalesce', ['get', 'lineWidth'], 3]
        ]
      }
    });
  }
};

export const drawPolygon = async (
  map: { current: maplibregl.Map | null },
  featuresArray: Feature<Geometry, GeoJsonProperties>[],
  uniqueId: string
) => {
  const existing = map.current?.getSource(uniqueId) as maplibregl.GeoJSONSource | undefined;
  if (existing) {
    existing.setData(makeFeatureCollection(featuresArray));
  } else {
    map.current?.addSource(uniqueId, {
      type: 'geojson',
      promoteId: 'uniqueFeatureId',
      data: makeFeatureCollection(featuresArray)
    });
  }

  if (!map.current?.getLayer(`${uniqueId}-fill`)) {
    map.current?.addLayer({
      id: `${uniqueId}-fill`,
      type: 'fill',
      source: uniqueId,
      minzoom: LAYER_VISIBILITY_ZOOM.polygon.minzoom,
      maxzoom: LAYER_VISIBILITY_ZOOM.polygon.maxzoom,
      layout: {},
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          // En min zoom, es completamente opaco
          LAYER_VISIBILITY_ZOOM.polygon.minzoom,
          1,
          // En el máximo zoom es 0.3 de opacidad
          LAYER_VISIBILITY_ZOOM.polygon.maxzoom,
          0.3
        ]
      }
    });
  }

  if (!map.current?.getLayer(`${uniqueId}-border`)) {
    map.current?.addLayer({
      id: `${uniqueId}-border`,
      type: 'line',
      source: uniqueId,
      minzoom: LAYER_VISIBILITY_ZOOM.polygon.minzoom,
      maxzoom: LAYER_VISIBILITY_ZOOM.polygon.maxzoom,
      layout: {},
      paint: {
        'line-color': '#000000',
        'line-width': ['case', ['boolean', ['feature-state', 'selected'], false], 2, 0]
      }
    });
  }
};

export const drawPoints = async (
  map: { current: maplibregl.Map | null },
  featuresArray: Feature<Geometry, GeoJsonProperties>[],
  artifactIconDictionary: ArtifactIconLinkType,
  uniqueId: string
) => {
  /* const radius = 0.1; // kilometer
   * const options = {
   *   steps: 64,
   *   units: 'kilometers'
   * };
   * const circle = turf.circle(radiusCenter, radius, options); */

  const existing = map.current?.getSource(uniqueId) as maplibregl.GeoJSONSource | undefined;
  if (existing) {
    existing.setData(makeFeatureCollection(featuresArray));
  } else {
    map.current?.addSource(uniqueId, {
      type: 'geojson',
      promoteId: 'uniqueId',
      data: makeFeatureCollection(featuresArray)
    });
  }

  const artifactNames = Object.keys(artifactIconDictionary);

  for (let i = 0; i < artifactNames.length; i++) {
    const artifactName = artifactNames[i];
    const image = await map.current?.loadImage(artifactIconDictionary[artifactName]);

    const marker = `marker-${artifactName}`;
    if (image && !map.current?.listImages().includes(marker)) {
      // NOTE: When the geoJSON is generated, in "properties", each artifact has its markerName as "marker-${artifactName}"
      map.current?.addImage(marker, image.data);
    } else {
      continue;
    }
  }

  if (map.current?.getSource(uniqueId) && !map.current?.getLayer(uniqueId)) {
    map.current?.addLayer({
      id: uniqueId,
      type: 'symbol',
      source: uniqueId,
      minzoom: LAYER_VISIBILITY_ZOOM.points.minzoom,
      layout: {
        'icon-image': ['get', 'markerName'],
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0,
          ['case', ['boolean', ['get', 'highlighted'], false], 0.09, 0.05],
          22,
          ['case', ['boolean', ['get', 'highlighted'], false], 0.5, 0.3]
        ],
        'icon-allow-overlap': true
      }
    });
  }

  const highlightLayerId = `${uniqueId}-highlight-ring`;
  if (map.current?.getSource(uniqueId) && !map.current?.getLayer(highlightLayerId)) {
    map.current?.addLayer(
      {
        id: highlightLayerId,
        type: 'circle',
        source: uniqueId,
        minzoom: LAYER_VISIBILITY_ZOOM.points.minzoom,
        filter: ['boolean', ['get', 'highlighted'], false],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 16, 8, 22, 22],
          'circle-color': 'transparent',
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#e06c00',
          'circle-stroke-opacity': 0.85
        }
      },
      uniqueId
    );
  }

  // Capa de impacto del proyecto: aro rojo sobre transformadores afectados.
  // Solo se activa cuando un feature tiene `impactoSnapshot: true` (propiedad
  // exclusiva de MapaImpacto, cubre snapshots normativos y flujos diarios).
  // El id de la capa MapLibre es legacy (anterior a NORM-038); se mantiene
  // para evitar tocar utilidades genéricas. Para el resto del mapa esta capa
  // existe pero es invisible — ningún feature tiene esa propiedad.
  const impactoRingId = 'impacto-snapshot-trafo-ring';
  if (map.current?.getSource(uniqueId) && !map.current?.getLayer(impactoRingId)) {
    map.current?.addLayer(
      {
        id: impactoRingId,
        type: 'circle',
        source: uniqueId,
        minzoom: LAYER_VISIBILITY_ZOOM.points.minzoom,
        filter: ['==', ['get', 'impactoSnapshot'], true],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 16, 12, 22, 28],
          'circle-color': 'transparent',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#e53935',
          'circle-stroke-opacity': 0.95
        }
      },
      uniqueId
    );
  }
};
